import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runContentPlanner } from "@/api/planner";
import { jobsQueries } from "@/api/scheduledJobs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Brain, Clock, Loader2 } from "lucide-react";
import { Persona } from "@/api/personas";

interface PlanContentDialogProps {
  onClose: () => void;
  personas: Persona[];
  selectedPersonaId: string;
}

export function PlanContentDialog({
  onClose,
  personas,
  selectedPersonaId,
}: PlanContentDialogProps) {
  const { toast } = useToast();
  const [isPlanning, setIsPlanning] = useState(false);
  const queryClient = useQueryClient();

  const selectedPersona = personas.find(
    (p) => p.persona_id === selectedPersonaId,
  );

  const plannerMutation = useMutation({
    mutationFn: () => runContentPlanner(selectedPersonaId, false),
    onSuccess: async () => {
      await queryClient.invalidateQueries(
        jobsQueries.persona(selectedPersonaId),
      );
      toast({
        title: "Success",
        description: "Content plan generated successfully",
      });
      onClose();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to generate content plan",
        variant: "destructive",
      });
    },
  });

  const handlePlanContent = async () => {
    try {
      setIsPlanning(true);
      await plannerMutation.mutateAsync();
    } finally {
      setIsPlanning(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Generate Content Schedule
          </DialogTitle>
          <DialogDescription>
            Let AI optimize your content schedule for {selectedPersona?.name}.
            This will analyze your automation settings and create an intelligent
            content plan for maximum engagement throughout the day.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <Card className="p-4 space-y-4 bg-[color:rgba(255,255,255,0.05)]">
            {isPlanning ? (
              <div className="flex flex-col items-center justify-center p-6">
                <Loader2 className="w-20 h-20 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground text-center mt-4">
                  Planning your content schedule...
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <Brain className="h-5 w-5 mt-1 text-primary" />
                <div>
                  <h3 className="font-semibold">AI-Powered Scheduling</h3>
                  <p className="text-sm text-muted-foreground">
                    The AI planner will:
                  </p>
                  <ul className="text-sm text-muted-foreground mt-2 space-y-2">
                    <li>• Analyze your persona's engagement patterns</li>
                    <li>• Optimize posting times for maximum reach</li>
                    <li>• Balance content types across your schedule</li>
                    <li>• Adapt to your timezone and audience activity</li>
                  </ul>
                </div>
              </div>
            )}
          </Card>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>
              Schedule will be generated for the remainder of existing operating
              window or upcoming window
            </span>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={isPlanning}>
              Cancel
            </Button>
            <Button
              onClick={handlePlanContent}
              disabled={isPlanning}
              className="min-w-[140px]"
            >
              {isPlanning ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </span>
              ) : (
                "Generate Schedule"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
